<template>
  <v-card
    class="voice-recorder-card ma-auto overflow-hidden"
    :max-width="$vuetify.breakpoint.xs ? '100%' : '380'"
    elevation="12"
    rounded="xl"
  >
    <v-card-text class="pa-4 d-flex flex-column justify-center fill-height">
      <div v-if="state === 'idle'" class="fade-in text-center py-2">
        <div class="mic-trigger-wrapper mb-3">
          <div class="pulse-ring-active"></div>
          <v-btn
            fab
            height="70"
            width="70"
            elevation="6"
            @click="startRecording"
            class="main-mic-btn btn-upload rounded-pill"
          >
            <v-icon size="35" color="white">mdi-microphone</v-icon>
          </v-btn>
        </div>

        <div class="text-h6 font-weight-black mb-1">ضبط صدا</div>
        <div class="caption grey--text text--darken-1">
          {{ recordInstruction }}
        </div>
      </div>

      <div v-if="state === 'recording'" class="fade-in">
        <div class="d-flex justify-space-between align-center mb-2 px-1">
          <div class="status-indicator">
            <span class="red-dot"></span>
            <span class="timer-mono">{{ formattedTime }}</span>
          </div>
          <v-btn icon x-small @click="cancel" color="grey"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </div>

        <div class="wave-container-mini mb-3">
          <canvas
            ref="waveform"
            width="340"
            height="60"
            class="wave-canvas"
          ></canvas>
        </div>

        <div class="text-center">
          <v-btn
            fab
            class="stop-pulse-btn btn-delete rounded-pill"
            @click="stopRecording"
          >
            <v-icon size="28">mdi-stop</v-icon>
          </v-btn>
        </div>
      </div>

      <div v-if="state === 'preview'" class="fade-in py-1">
        <div class="compact-player pa-2 mb-3 d-flex align-center" dir="ltr">
          <v-btn
            fab
            depressed
            width="40"
            height="40"
            @click="togglePlayback"
            class="btn-upload rounded-pill"
          >
            <v-icon size="20">{{
              isPlaying ? "mdi-pause" : "mdi-play"
            }}</v-icon>
          </v-btn>

          <div class="flex-grow-1 mx-3">
            <!-- <v-slider
              v-model="playbackProgress"
              :max="audioDuration || 1"
              step="0.01"
              hide-details
              dense
              class="narrow-slider"
              @change="seekAudio"
            ></v-slider> -->
            <input
              type="range"
              v-model.number="playbackProgress"
              :max="audioDuration || 1"
              step="0.01"
              class="custom-audio-slider mb-1"
              @input="seekAudio($event.target.value)"
              :style="{
                '--progress': audioDuration
                  ? (playbackProgress / audioDuration) * 100 + '%'
                  : '0%',
              }"
            />
            <div
              class="d-flex justify-space-between mt-n1 caption mono-font grey--text text--darken-2"
            >
              <span>{{ formattedPlaybackTime }}</span>
              <span>{{ formatDuration(audioDuration) }}</span>
            </div>
          </div>
        </div>

        <div class="action-buttons-row">
          <v-btn
            outlined
            class="rounded-lg flex-grow-1 btn-delete"
            @click="deleteRecording"
            small
          >
            <v-icon left size="16">mdi-delete-outline</v-icon> حذف
          </v-btn>

          <v-btn
            depressed
            class="rounded-lg btn-upload shadow-sm flex-grow-2"
            @click="attach"
            small
          >
            <v-icon left size="16">mdi-send</v-icon> تایید و ارسال
          </v-btn>
        </div>
      </div>
    </v-card-text>

    <audio
      v-if="audioUrl"
      ref="audioPreview"
      :src="audioUrl"
      @timeupdate="updateProgress"
      @loadedmetadata="setDuration"
      @ended="onAudioEnded"
    ></audio>
  </v-card>
</template>

<style scoped>
.voice-recorder-card {
  min-height: 200px; /* باز هم کوتاه‌تر برای مدرن بودن */
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.mic-trigger-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 90px;
}

.main-mic-btn {
  background: linear-gradient(135deg, #001554, #001a6867) !important;
  z-index: 2;
}

.pulse-ring-active {
  position: absolute;
  width: 75px;
  height: 75px;
  border: 2px solid #001554;
  border-radius: 50%;
  animation: ripple-effect 2s infinite ease-out;
}

.wave-container-mini {
  background: #f8fafc;
  border-radius: 12px;
  height: 65px;
  display: flex;
  align-items: center;
  padding: 0 5px;
}

.status-indicator {
  display: flex;
  align-items: center;
  background: #fff5f5;
  padding: 0 10px;
  border-radius: 20px;
}

.timer-mono {
  font-family: "monospace";
  font-weight: bold;
  font-size: 1.1rem;
  color: #d32f2f;
}

.red-dot {
  width: 8px;
  height: 8px;
  background: #ff5252;
  border-radius: 50%;
  margin-left: 6px;
  animation: blink-soft 1s infinite;
}

.compact-player {
  background: #f1f5f9;
  border-radius: 14px;
}

.action-buttons-row {
  display: flex;
  gap: 10px;
}

.flex-grow-2 {
  flex: 2.5;
}

.gradient-btn {
  background: linear-gradient(90deg, #001554, #00155438) !important;
  font-weight: bold;
}

@keyframes ripple-effect {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

@keyframes blink-soft {
  50% {
    opacity: 0.2;
  }
}

.fade-in {
  animation: fadeInQuick 0.3s ease-out;
}

@keyframes fadeInQuick {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.narrow-slider >>> .v-slider__thumb {
  width: 10px;
  height: 10px;
}
</style>

<script>
export default {
  props: {
    recordInstruction: { type: String, default: "برای شروع ضبط ضربه بزنید" },
  },
  data() {
    return {
      state: "idle",
      mediaRecorder: null,
      audioChunks: [],
      audioUrl: null,
      audioDuration: 0,
      playbackProgress: 0,
      recordingTime: 0,
      timerInterval: null,
      audioContext: null,
      analyser: null,
      animationFrame: null,
      isPlaying: false,
    };
  },
  computed: {
    formattedTime() {
      const min = Math.floor(this.recordingTime / 60);
      const sec = Math.floor(this.recordingTime % 60);
      return `${min}:${sec.toString().padStart(2, "0")}`;
    },
    formattedPlaybackTime() {
      const min = Math.floor(this.playbackProgress / 60);
      const sec = Math.floor(this.playbackProgress % 60);
      return `${min}:${sec.toString().padStart(2, "0")}`;
    },
  },
  methods: {
    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        this.mediaRecorder = new MediaRecorder(stream);
        this.audioChunks = [];
        this.recordingTime = 0;
        this.mediaRecorder.ondataavailable = (e) =>
          this.audioChunks.push(e.data);
        this.mediaRecorder.onstop = this.handleStopRecording;
        this.mediaRecorder.start();
        this.state = "recording";
        this.startTimer();
        this.$nextTick(() => this.startWaveformAnimation(stream));
      } catch (err) {
        this.$emit("error", "خطا در دسترسی به میکروفون");
      }
    },
    stopRecording() {
      if (this.mediaRecorder?.state === "recording") {
        this.mediaRecorder.stop();
        this.mediaRecorder.stream.getTracks().forEach((t) => t.stop());
      }
      this.stopTimer();
      this.stopWaveformAnimation();
    },
    handleStopRecording() {
      const blob = new Blob(this.audioChunks, { type: "audio/ogg" });
      this.audioUrl = URL.createObjectURL(blob);
      this.state = "preview";
    },
    startWaveformAnimation(stream) {
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const source = this.audioContext.createMediaStreamSource(stream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 64;
      source.connect(this.analyser);
      const bufferLen = this.analyser.frequencyBinCount;
      const dataArr = new Uint8Array(bufferLen);
      const canvas = this.$refs.waveform;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");

      const draw = () => {
        this.animationFrame = requestAnimationFrame(draw);
        this.analyser.getByteFrequencyData(dataArr);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const barWidth = (canvas.width / bufferLen) * 2;
        let x = 0;
        for (let i = 0; i < bufferLen; i++) {
          const barHeight = (dataArr[i] / 255) * canvas.height;
          ctx.fillStyle = `#001554`;
          this.drawRoundedRect(
            ctx,
            x,
            (canvas.height - barHeight) / 2,
            barWidth - 2,
            barHeight,
            4,
          );
          x += barWidth;
        }
      };
      draw();
    },
    drawRoundedRect(ctx, x, y, width, height, radius) {
      if (height < radius * 2) radius = height / 2;
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.arcTo(x + width, y, x + width, y + height, radius);
      ctx.arcTo(x + width, y + height, x, y + height, radius);
      ctx.arcTo(x, y + height, x, y, radius);
      ctx.arcTo(x, y, x + width, y, radius);
      ctx.closePath();
      ctx.fill();
    },
    stopWaveformAnimation() {
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      if (this.audioContext && this.audioContext.state !== "closed") {
        this.audioContext.close().catch((e) => console.log(e));
      }
    },
    togglePlayback() {
      const audio = this.$refs.audioPreview;
      this.isPlaying ? audio.pause() : audio.play();
      this.isPlaying = !this.isPlaying;
    },
    updateProgress() {
      this.playbackProgress = this.$refs.audioPreview?.currentTime || 0;
    },
    setDuration() {
      this.audioDuration = this.$refs.audioPreview?.duration || 0;
    },
    seekAudio(val) {
      if (this.$refs.audioPreview) this.$refs.audioPreview.currentTime = val;
    },
    onAudioEnded() {
      this.isPlaying = false;
      this.playbackProgress = 0;
    },
    formatDuration(sec) {
      if (!sec || isNaN(sec)) return "0:00";
      return `${Math.floor(sec / 60)}:${Math.floor(sec % 60)
        .toString()
        .padStart(2, "0")}`;
    },
    startTimer() {
      this.timerInterval = setInterval(() => {
        this.recordingTime += 1;
      }, 1000);
    },
    stopTimer() {
      clearInterval(this.timerInterval);
    },
    deleteRecording() {
      this.resetAll();
    },
    cancel() {
      this.stopRecording();
      this.resetAll();
      this.$emit("cancel");
    },
    attach() {
      const blob = new Blob(this.audioChunks, { type: "audio/ogg" });
      this.$emit("recordingComplete", blob);
      this.resetAll();
    },
    resetAll() {
      this.stopTimer();
      this.stopWaveformAnimation();
      if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
      this.state = "idle";
      this.audioUrl = null;
      this.audioChunks = [];
      this.isPlaying = false;
      this.recordingTime = 0;
    },
  },
};
</script>
<style scoped>
.custom-audio-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100% !important;
  height: 4px;
  border-radius: 4px;
  outline: none;
  direction: ltr !important;
  background: linear-gradient(
    to right,
    #001554 var(--progress),
    #cbd5e1 var(--progress)
  );
}

.custom-audio-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #001554;
  cursor: pointer;
  transition: transform 0.1s;
}

.custom-audio-slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
}
.custom-audio-slider::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #001554;
  cursor: pointer;
  border: none;
}
</style>
